import { useEffect, useState, useCallback } from 'react';
import { toast } from 'react-toastify';
import { Spinner } from '../../components/ui';
import { adminAPI } from '../../api';
import { BUCKETS } from '../../theme';

const PERIODS = [
  { k: '12w',  l: '12 weeks' },
  { k: 'term', l: 'Term vs term' },
  { k: 'year', l: 'Year vs year' },
];

const pctColor = (pct) =>
  pct >= 70 ? 'var(--teal)' : pct >= 50 ? 'var(--amber)' : 'var(--red)';

function Delta({ value }) {
  if (value === null || value === undefined) return <span className="dl dl-none">No comparison</span>;
  const cls = value > 0 ? 'dl-up' : value < 0 ? 'dl-down' : 'dl-flat';
  return <span className={`dl ${cls}`}>{value > 0 ? '↑ ' : value < 0 ? '↓ ' : ''}{Math.abs(value)}</span>;
}

/** School and district dashboards, for any school or district. */
export default function DashboardsTab({ onSetFocus }) {
  const [scope, setScope]     = useState('school');
  const [period, setPeriod]   = useState('term');
  const [schools, setSchools] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [target, setTarget]   = useState('');
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(false);
  const [focusSaving, setFocusSaving] = useState(false);

  useEffect(() => {
    Promise.all([adminAPI.schools(), adminAPI.districts()])
      .then(([s, d]) => {
        setSchools(s.data); setDistricts(d.data);
        if (s.data.length) setTarget(s.data[0].id);
      })
      .catch(() => toast.error('Could not load schools.'));
  }, []);

  // Changing scope changes what the target list means.
  useEffect(() => {
    const list = scope === 'school' ? schools : districts;
    setTarget(list.length ? list[0].id : '');
    setData(null);
  }, [scope, schools, districts]);

  const load = useCallback(() => {
    if (!target) return;
    setLoading(true);
    const call = scope === 'school'
      ? adminAPI.schoolDashboard(target, { period })
      : adminAPI.districtDashboard(target, { period });
    call
      .then(({ data }) => setData(data))
      .catch(err => {
        console.error(err);
        toast.error(err?.response?.status === 403
          ? 'You do not have access to that dashboard.'
          : 'Could not load that dashboard.');
        setData(null);
      })
      .finally(() => setLoading(false));
  }, [scope, target, period]);

  useEffect(load, [load]);

  const setFocus = async (bucketKey) => {
    setFocusSaving(true);
    try {
      await adminAPI.setFocus(target, bucketKey);
      toast.success(bucketKey ? 'Focus bucket set.' : 'Focus bucket cleared.');
      load();
      onSetFocus?.();
    } catch {
      toast.error('Could not set the focus bucket.');
    } finally { setFocusSaving(false); }
  };

  const list = scope === 'school' ? schools : districts;

  return (
    <>
      <div className="adm-toolbar">
        <select className="fi" value={scope} onChange={e => setScope(e.target.value)}>
          <option value="school">School dashboard</option>
          <option value="district">District dashboard</option>
        </select>
        <select className="fi" value={target} onChange={e => setTarget(e.target.value)}>
          {list.map(x => <option key={x.id} value={x.id}>{x.name}</option>)}
        </select>
      </div>

      <div className="adm-toolbar">
        {PERIODS.map(p => (
          <button key={p.k} className={`adm-chip${period === p.k ? ' on' : ''}`}
                  onClick={() => setPeriod(p.k)}>{p.l}</button>
        ))}
      </div>

      {loading && (
        <div style={{ display:'flex', justifyContent:'center', padding:'32px 0' }}><Spinner /></div>
      )}

      {!loading && data && !data.has_data && (
        <div className="dash-empty">No observations in this period.</div>
      )}

      {!loading && data?.has_data && (
        <>
          <div className="adm-stats">
            <div className="adm-stat">
              <div className="adm-stat-n">{data.obs_count}</div>
              <div className="adm-stat-l">Observations</div>
              <Delta value={data.stats.observations.delta} />
            </div>
            <div className="adm-stat">
              <div className="adm-stat-n">{data.total_notes}</div>
              <div className="adm-stat-l">Evidence notes</div>
            </div>
          </div>

          {/* District view breaks out each school. */}
          {scope === 'district' && (
            <div className="adm-card">
              <div className="adm-card-title">By school</div>
              {data.schools.map(s => (
                <div key={s.id} className="adm-row">
                  <span>{s.name}</span>
                  <span className="adm-row-n">
                    {s.obs_count} obs · <span style={{ color: pctColor(s.pct) }}>{s.pct}%</span>
                  </span>
                </div>
              ))}
            </div>
          )}

          <div className="adm-card">
            <div className="adm-card-title">Learning patterns — by bucket</div>
            {data.buckets.map(b => (
              <div key={b.key} className="bbar-item">
                <div className="bbar-label">{b.label}</div>
                <div className="bbar-track">
                  <div className="bbar-fill" style={{ width:`${b.pct}%`, background:pctColor(b.pct) }} />
                </div>
                <div className="bbar-pct" style={{ color:pctColor(b.pct) }}>{b.pct}%</div>
              </div>
            ))}
          </div>

          {/* Focus bucket is a school-level decision. */}
          {scope === 'school' && (
            <div className="adm-card">
              <div className="adm-card-title">Focus bucket</div>
              <div className="adm-hint" style={{ marginLeft: 0, marginBottom: 10, display:'block' }}>
                The bucket this school is working on. It is recorded against every
                observation taken while it is set.
              </div>
              <div className="adm-chips">
                {Object.entries(BUCKETS).map(([key, b]) => (
                  <button key={key} disabled={focusSaving}
                          className={`adm-chip${data.focus?.key === key ? ' on' : ''}`}
                          onClick={() => setFocus(key)}>
                    {b.name}
                  </button>
                ))}
                {data.focus && (
                  <button className="adm-chip" disabled={focusSaving} onClick={() => setFocus('')}>
                    Clear
                  </button>
                )}
              </div>
            </div>
          )}
        </>
      )}
    </>
  );
}
